/**
 * Ausrichtung eines gezogenen Notiz an den anderen — Kante an Kante,
 * Mitte an Mitte, oder mit festem Abstand daneben. Siehe „Einrasten" und
 * „Hilfslinie" in CONTEXT.md.
 *
 * Bewusst ohne React-Bezug: die Kernlogik ist Geometrie und soll ohne
 * gerenderten Canvas prüfbar sein.
 */

/**
 * Fangschwelle in BILDSCHIRM-Pixeln. Der Aufrufer teilt sie durch den Zoom,
 * damit sich das Einrasten bei jeder Zoomstufe gleich anfühlt — bei 30 %
 * entspricht sie sonst nur 2,4 Bildschirmpixeln und wäre kaum zu treffen.
 */
import { getSpacingCandidates, type SpacingCandidate } from './canvasSpacing'

export const ALIGNMENT_THRESHOLD = 8

/**
 * Abstand, auf den beim Anlegen an eine fremde Kante eingerastet wird.
 * Bewusst nicht bündig (0): so ausgelegte Notizen behalten eine sichtbare
 * Lücke, wie Zettel auf einem Tisch.
 */
export const ALIGNMENT_GAP = 24

/** Die Kanten eines Elements auf dem Canvas. */
export interface Bounds {
  left: number
  right: number
  centerX: number
  top: number
  bottom: number
  centerY: number
}

/** Eine Hilfslinie in Canvas-Koordinaten. */
export interface Guide {
  /** Fester Wert: x bei senkrechter, y bei waagerechter Linie. */
  position: number
  /** Ausdehnung auf der anderen Achse — äußere Kanten aller Beteiligten. */
  start: number
  end: number
}

export interface AlignmentResult {
  position: { x: number; y: number }
  /**
   * Die Klammern für den gleichen Abstand — leer, wenn keine Achse darüber
   * eingerastet ist. Beide Achsen können welche beisteuern.
   */
  measures: Measure[]
  /**
   * Senkrechte Linien. Mehrere, weil bei fluchtenden Kanten oft zugleich die
   * linke UND die rechte übereinstimmen — beide gehören gezeigt.
   */
  vertical: Guide[]
  /** Waagerechte Linien, dasselbe für oben/unten/Mitte. */
  horizontal: Guide[]
}

export interface MovingRect {
  x: number
  y: number
  width: number
  height: number
}

/** Rundet auf das Punktraster — dieselbe Rechnung, die React Flow verwendet. */
export function snapToGrid(value: number, grid: number): number {
  return grid > 0 ? grid * Math.round(value / grid) : value
}

/**
 * Ob ein Element den sichtbaren Ausschnitt berührt. Schon eine Überlappung
 * genügt: eine Notiz, die halb am Rand hängt, sieht man ja — an ihr soll man
 * sich ausrichten können.
 */
export function isVisible(bounds: Bounds, viewport: Bounds): boolean {
  return (
    bounds.right >= viewport.left &&
    bounds.left <= viewport.right &&
    bounds.bottom >= viewport.top &&
    bounds.top <= viewport.bottom
  )
}

/**
 * Ob ein Element GANZ im sichtbaren Ausschnitt liegt — jede Kante innerhalb.
 *
 * Die schärfere Schwester von `isVisible`, und die beiden meinen bewusst
 * Verschiedenes: Zum Ausrichten genügt es, wenn man ein Element sieht (dort
 * `isVisible`); um es anzusteuern, muss man es GANZ sehen. Eine Notiz, von dem
 * ein Zipfel am Rand hängt, ist angekommen und trotzdem nicht angeschaut.
 *
 * Dieselbe Unterscheidung fehlt React Flow: Sein `autoPanOnNodeFocus` fragt
 * `getNodesInside(..., partially: true)` und hält damit schon ein sichtbares
 * Eckchen für genug.
 *
 * Ein Element, das größer ist als der Ausschnitt, kann diese Prüfung nie
 * bestehen. Das ist richtig so: Ganz zu sehen ist es dann nirgends, und der
 * Aufrufer tut das Bestmögliche — es mittig stellen.
 */
export function isFullyVisible(bounds: Bounds, viewport: Bounds): boolean {
  return (
    bounds.left >= viewport.left &&
    bounds.right <= viewport.right &&
    bounds.top >= viewport.top &&
    bounds.bottom <= viewport.bottom
  )
}

/** Die Kanten eines Rechtecks. */
export function toBounds(rect: MovingRect): Bounds {
  return {
    left: rect.x,
    right: rect.x + rect.width,
    centerX: rect.x + rect.width / 2,
    top: rect.y,
    bottom: rect.y + rect.height,
    centerY: rect.y + rect.height / 2,
  }
}

/** Das umschließende Rechteck mehrerer Elemente, oder `null` bei keinem. */
export function getBoundingBox(rects: MovingRect[]): Bounds | null {
  if (rects.length === 0) return null

  let left = Infinity
  let right = -Infinity
  let top = Infinity
  let bottom = -Infinity

  for (const rect of rects) {
    left = Math.min(left, rect.x)
    right = Math.max(right, rect.x + rect.width)
    top = Math.min(top, rect.y)
    bottom = Math.max(bottom, rect.y + rect.height)
  }

  return {
    left,
    right,
    centerX: (left + right) / 2,
    top,
    bottom,
    centerY: (top + bottom) / 2,
  }
}

/** Ein geprüfter Kandidat: wohin die bewegte Kante rasten würde. */
interface Candidate {
  /** Die Position, die das Element bekäme (linke bzw. obere Kante). */
  position: number
  /** Abstand zur aktuellen Lage — kleiner ist besser. */
  distance: number
  /**
   * Die Klammern, die diesen Kandidaten begründen — nur bei gleichem Abstand.
   * Kanten-Kandidaten haben keine: Ihre Hilfslinien sammelt `collectGuides`
   * nachträglich aus der ENDGÜLTIGEN Lage, nicht aus dem Kandidaten.
   */
  measures?: Measure[]
}

/**
 * Sucht je Achse den nächstgelegenen Kandidaten. Getrennte Funktion, weil x
 * und y dieselbe Rechnung mit anderen Feldern sind — einmal geschrieben statt
 * zweimal.
 *
 * @param lead    aktuelle Position der führenden Kante (links bzw. oben)
 * @param size    Breite bzw. Höhe des bewegten Elements
 * @param spacing Kandidaten für den gleichen Abstand (siehe `canvasSpacing`)
 */
function findAxisAlignment(
  lead: number,
  size: number,
  others: Bounds[],
  pick: (b: Bounds) => { lead: number; trail: number; center: number },
  threshold: number,
  spacing: SpacingCandidate[],
): Candidate | null {
  const trail = lead + size
  const center = lead + size / 2
  let best: Candidate | null = null

  const consider = (position: number, distance: number, measures?: Measure[]) => {
    if (distance > threshold) return
    // `<` statt `<=`: bei Gleichstand gewinnt der zuerst geprüfte Kandidat.
    // Die Reihenfolge unten ist deshalb eine Rangfolge.
    if (!best || distance < best.distance) best = { position, distance, measures }
  }

  for (const other of others) {
    const o = pick(other)

    // Fluchten: gleiche Kante, gleiche Mitte. Die stärkste Aussage — sie ist
    // unmittelbar zu sehen.
    consider(o.lead, Math.abs(lead - o.lead))
    consider(o.trail - size, Math.abs(trail - o.trail))
    consider(o.center - size / 2, Math.abs(center - o.center))
  }

  // Gleicher Abstand. Steht VOR dem festen Anlegen: Beide sagen „so weit weg",
  // aber der gleiche Abstand begründet sich aus dem, was schon daliegt — das
  // feste Maß nur aus sich selbst. Bei Gleichstand gewinnt also der Abstand.
  for (const candidate of spacing) {
    consider(candidate.position, candidate.distance, candidate.measures)
  }

  for (const other of others) {
    const o = pick(other)

    // Anlegen: mit festem Abstand neben das andere Element.
    const after = o.trail + ALIGNMENT_GAP
    consider(after, Math.abs(lead - after))
    const before = o.lead - ALIGNMENT_GAP
    consider(before - size, Math.abs(trail - before))
  }

  return best
}

/**
 * Zwei Koordinaten gelten als gleich, wenn sie unter einem halben Pixel
 * auseinanderliegen.
 *
 * Exportiert, weil das **Platz tauschen** (`canvasSwap`) dieselbe Frage stellt
 * wie die Hilfslinien: „fluchten die beiden hier?". Zwei Toleranzen für
 * dieselbe Aussage liefen unweigerlich auseinander.
 */
export function same(a: number, b: number): boolean {
  return Math.abs(a - b) < 0.5
}

/**
 * Sammelt ALLE Linien einer Achse, die bei der endgültigen Position
 * tatsächlich fluchten — nicht nur die, an der eingerastet wurde.
 *
 * Zwei gleich hohe Notizen nebeneinander stimmen an der Ober- UND der
 * Unterkante überein; beide Linien gehören gezeigt, sonst sieht man nur die
 * halbe Wahrheit.
 *
 * @param lead   führende Kante des bewegten Elements (links bzw. oben)
 * @param size   Breite bzw. Höhe des bewegten Elements
 * @param pick   die drei Ausrichtungswerte eines anderen Elements
 * @param extent Ausdehnung eines Elements auf der LINIEN-Achse
 */
function collectGuides(
  lead: number,
  size: number,
  others: Bounds[],
  pick: (b: Bounds) => { lead: number; trail: number; center: number },
  extent: (b: Bounds) => { start: number; end: number },
  movingExtent: { start: number; end: number },
): Guide[] {
  const trail = lead + size
  const center = lead + size / 2
  // Nach Linienposition gruppieren: liegen fünf Notizen auf einer Kante,
  // wird daraus EINE Linie über alle fünf, nicht fünf übereinander.
  // `roles` hält fest, WELCHE Kante des bewegten Elements die Linie erzeugt
  // hat — das entscheidet unten, ob die Mittellinie entbehrlich ist.
  const byPosition = new Map<
    number,
    { start: number; end: number; roles: Set<'lead' | 'trail' | 'center'> }
  >()

  const add = (position: number, other: Bounds, role: 'lead' | 'trail' | 'center') => {
    const e = extent(other)
    const existing = byPosition.get(position)
    if (existing) {
      existing.start = Math.min(existing.start, e.start)
      existing.end = Math.max(existing.end, e.end)
      existing.roles.add(role)
    } else {
      byPosition.set(position, {
        start: Math.min(movingExtent.start, e.start),
        end: Math.max(movingExtent.end, e.end),
        roles: new Set([role]),
      })
    }
  }

  const mine = [
    { value: lead, role: 'lead' as const },
    { value: trail, role: 'trail' as const },
    { value: center, role: 'center' as const },
  ]

  for (const other of others) {
    const o = pick(other)

    // Fluchten: jede Kante und die Mitte des bewegten Elements gegen jede des
    // anderen. Alle neun Paare, damit auch „meine Unterkante auf deiner
    // Oberkante" eine Linie bekommt.
    for (const m of mine) {
      for (const theirs of [o.lead, o.trail, o.center]) {
        if (same(m.value, theirs)) add(theirs, other, m.role)
      }
    }

    // Anlegen: die Linie liegt mittig im Spalt, es gibt ja keine gemeinsame
    // Kante.
    if (same(lead, o.trail + ALIGNMENT_GAP)) add(o.trail + ALIGNMENT_GAP / 2, other, 'lead')
    if (same(trail, o.lead - ALIGNMENT_GAP)) add(o.lead - ALIGNMENT_GAP / 2, other, 'trail')
  }

  // Fluchten beide Kanten, sagt die Mittellinie nichts Neues mehr — sie liegt
  // zwangsläufig dazwischen und macht aus zwei klaren Linien einen Dreisatz.
  //
  // Sie fällt auch dann weg, wenn dort zufällig die Kante eines DRITTEN
  // Notiz liegt; diese Beziehung bleibt dann unsichtbar. Bewusst in Kauf
  // genommen: die Regel „zwei Kantenlinien, keine Mitte" versteht man ohne
  // Nachdenken, die Ausnahme davon nicht.
  const hasLead = [...byPosition.values()].some(v => v.roles.has('lead'))
  const hasTrail = [...byPosition.values()].some(v => v.roles.has('trail'))
  if (hasLead && hasTrail) {
    for (const [position, entry] of byPosition) {
      if (entry.roles.size === 1 && entry.roles.has('center')) byPosition.delete(position)
    }
  }

  return [...byPosition.entries()].map(([position, span]) => ({
    position,
    start: span.start,
    end: span.end,
  }))
}

/**
 * Richtet ein gezogenes Element an den anderen aus. Findet eine Achse keinen
 * Kandidaten, rastet sie stattdessen auf das Punktraster — die Achsen werden
 * unabhängig entschieden, eine Notiz kann also waagerecht einrasten und
 * senkrecht frei bleiben.
 *
 * @param threshold  Fangschwelle in CANVAS-Pixeln (also `ALIGNMENT_THRESHOLD / zoom`)
 * @param grid       Rasterweite für Achsen ohne Ausrichtung
 */
export function getAlignment(
  moving: MovingRect,
  others: Bounds[],
  threshold: number,
  grid: number,
): AlignmentResult {
  const x = findAxisAlignment(
    moving.x,
    moving.width,
    others,
    b => ({ lead: b.left, trail: b.right, center: b.centerX }),
    threshold,
    getSpacingCandidates(moving, others, 'x'),
  ) as Candidate | null

  const y = findAxisAlignment(
    moving.y,
    moving.height,
    others,
    b => ({ lead: b.top, trail: b.bottom, center: b.centerY }),
    threshold,
    getSpacingCandidates(moving, others, 'y'),
  ) as Candidate | null

  const position = {
    x: x ? x.position : snapToGrid(moving.x, grid),
    y: y ? y.position : snapToGrid(moving.y, grid),
  }

  // Die Linien werden aus der ENDGÜLTIGEN Lage gesammelt, nicht aus der rohen
  // — sonst stünde die Linie einen Frame lang neben ihrem Ziel. Und nur, wenn
  // die Achse überhaupt eingerastet ist: nach reinem Rasterfang soll keine
  // Linie erscheinen, auch wenn zufällig etwas fluchtet.
  const movingV = { start: position.y, end: position.y + moving.height }
  const movingH = { start: position.x, end: position.x + moving.width }

  return {
    position,
    // Beide Achsen können Klammern beisteuern — waagerecht und senkrecht
    // gleichzeitig mittig zu liegen ist ein völlig normaler Fall.
    measures: [...(x?.measures ?? []), ...(y?.measures ?? [])],
    vertical: x
      ? collectGuides(
          position.x,
          moving.width,
          others,
          b => ({ lead: b.left, trail: b.right, center: b.centerX }),
          b => ({ start: b.top, end: b.bottom }),
          movingV,
        )
      : [],
    horizontal: y
      ? collectGuides(
          position.y,
          moving.height,
          others,
          b => ({ lead: b.top, trail: b.bottom, center: b.centerY }),
          b => ({ start: b.left, end: b.right }),
          movingH,
        )
      : [],
  }
}

/**
 * Die Linien und Klammern einer Ausrichtung, ohne deren Ergebnis. Als eigener
 * Typ, weil beide Gruppen-Gesten sie unverändert an `CanvasFlow` durchreichen:
 * `setGuides` gehört dem Canvas, nicht der Geometrie.
 */
export interface AlignmentLines {
  vertical: Guide[]
  horizontal: Guide[]
  measures: Measure[]
}

export interface GroupAlignmentResult extends AlignmentLines {
  /** Die neue Lage jedes Rechtecks, in der Reihenfolge der Eingabe. */
  rects: MovingRect[]
}

/**
 * Richtet eine ganze Auswahl beim Verschieben aus: Ihr **Auswahlrahmen** —
 * das umschließende Rechteck — übernimmt die Rolle des bewegten Elements. Der
 * gefundene Versatz wird auf ALLE Rechtecke angewandt, damit die Abstände
 * innerhalb der Auswahl unverändert bleiben.
 *
 * Jedes Element für sich auszurichten wäre der Fehler: Elemente mit
 * unterschiedlichem Rest sprängen unterschiedlich weit, die Anordnung zerfiele
 * — und Linien für Elemente, an denen nichts einrastet, behaupteten eine
 * Beziehung, die die Bewegung nicht herstellt.
 *
 * Bei EINEM Rechteck identisch mit `getAlignment` — die Verallgemeinerung,
 * keine zweite Wahrheit. Auch das Rastern ohne Treffer folgt daraus: Gerastert
 * wird die POSITION des Rahmens, nicht der Versatz, sonst käme eine schief
 * liegende Auswahl nie wieder auf die Punkte.
 *
 * @param delta der rohe Versatz der GESAMTEN Geste, nicht der eines Frames
 */
export function getGroupAlignment(
  rects: MovingRect[],
  delta: { x: number; y: number },
  others: Bounds[],
  threshold: number,
  grid: number,
): GroupAlignmentResult {
  const box = getBoundingBox(rects)
  if (!box) return { rects: [], vertical: [], horizontal: [], measures: [] }

  const result = getAlignment(
    {
      x: box.left + delta.x,
      y: box.top + delta.y,
      width: box.right - box.left,
      height: box.bottom - box.top,
    },
    others,
    threshold,
    grid,
  )

  // Der Versatz aus der Differenz, nicht aus `delta`: Er enthält die Korrektur
  // des Einrastens und gilt für alle gleich.
  const offset = { x: result.position.x - box.left, y: result.position.y - box.top }

  return {
    rects: rects.map(rect => ({ ...rect, x: rect.x + offset.x, y: rect.y + offset.y })),
    vertical: result.vertical,
    horizontal: result.horizontal,
    measures: result.measures,
  }
}

/* ------------------------------------------------------------------------ *
 * Ausrichten beim Ändern der Größe
 * ------------------------------------------------------------------------ */

/** Die vier Ecken, an denen sich eine Notiz aufziehen lässt. */
/**
 * Woran gezogen wird — die Frage, an der die Rechnung sich scheidet.
 *
 * `'corner'`: seitenverhältnistreu, Breite und Höhe zusammen
 * (`getResizeAlignment`). `'edge'`: allein die Breite
 * (`getWidthResizeAlignment`), Höhe und die gegenüberliegende Kante bleiben
 * stehen.
 *
 * Bewusst NICHT an der Art des Elements festgemacht: Der **Notiz** hat
 * inzwischen beides — Ecken zum Skalieren und Kanten für seine zweite Breite.
 */
/** `free`: beide Maße unabhängig, kein Seitenverhältnis — der **Bereich**. */
export type ResizeMode = 'corner' | 'edge' | 'free'

export type ResizeCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

/**
 * Die beiden Kanten, die der Zeiger führt. Die jeweils gegenüberliegende
 * bleibt stehen und dient als Anker.
 */
export interface MovingEdges {
  x: 'left' | 'right'
  y: 'top' | 'bottom'
}

/** Welche Kanten eine angefasste Ecke bewegt. */
export function movingEdgesFor(corner: ResizeCorner): MovingEdges {
  return {
    x: corner.endsWith('left') ? 'left' : 'right',
    y: corner.startsWith('top') ? 'top' : 'bottom',
  }
}

/**
 * Eine Maßlinie: die Klammer, die die Breite oder Höhe einer Notiz zeigt.
 * Es gibt sie immer im Paar — an der gezogenen und an der Partner-Notiz —,
 * denn eine einzelne Klammer sagt nichts darüber, WOMIT die Größe übereinstimmt.
 */
export interface Measure {
  /** `x` misst waagerecht (Breite), `y` senkrecht (Höhe). */
  axis: 'x' | 'y'
  /** Anfang und Ende der Klammer auf ihrer Achse. */
  from: number
  to: number
  /**
   * Die Querkoordinate, auf der die Klammer sitzt. Wie sie dort ausgerichtet
   * wird, sagt `placement`.
   */
  at: number
  /**
   * WAS die Klammer misst. Steuert beides, Lage und Farbe:
   *
   * - `'size'` (Vorgabe) — die Größe einer Notiz, beim Skalieren. Sitzt
   *   16px neben der Kante (unter den Eckpunkten hindurch) und ist blau wie
   *   die übrigen Hilfslinien.
   * - `'gap'` — eine Lücke zwischen zwei Notizen, beim gleichen Abstand.
   *   Sitzt genau auf `at` (das liegt schon mittig im Band) und ist violett:
   *   „hier stimmen zwei Lücken überein" ist eine andere Aussage als „hier
   *   fluchtet eine Kante".
   */
  kind?: 'size' | 'gap'
}

export interface ResizeAlignmentResult {
  /** Die eingerastete Lage und Größe. */
  rect: MovingRect
  /**
   * Ob überhaupt eine Ausrichtung gegriffen hat — sonst war es reines Rastern.
   * Am Ergebnis allein nicht abzulesen, und die Mehrfachauswahl entscheidet
   * daran, ob sie die RAHMENKANTE übernimmt oder ihren FAKTOR rastert.
   */
  snapped: boolean
  vertical: Guide[]
  horizontal: Guide[]
  measures: Measure[]
}

/** Grenzen, in denen die eingerastete Größe bleiben muss. */
export interface SizeLimits {
  min: number
  max: number
}

/** Eine geprüfte Zielbreite und der Weg, den der Zeiger dafür nachgäbe. */
interface WidthCandidate {
  width: number
  distance: number
}

/** Die Werte, an denen eine Kante auf einer Achse einrasten kann. */
function edgeTargets(
  o: Bounds,
  axis: 'x' | 'y',
  moving: MovingEdges,
): { match: number; guideAt: number }[] {
  const [lead, trail, center] =
    axis === 'x' ? [o.left, o.right, o.centerX] : [o.top, o.bottom, o.centerY]
  // Anlegen mit festem Abstand: die führende Kante hinter das andere Element,
  // die folgende davor. Nur die passende Richtung — die andere Variante hieße,
  // eine rechte Kante 24px RECHTS neben einer rechten Kante einzurasten, und
  // das ist keine sichtbare Beziehung.
  const attach =
    (axis === 'x' ? moving.x === 'left' : moving.y === 'top')
      ? { match: trail + ALIGNMENT_GAP, guideAt: trail + ALIGNMENT_GAP / 2 }
      : { match: lead - ALIGNMENT_GAP, guideAt: lead - ALIGNMENT_GAP / 2 }

  return [
    { match: lead, guideAt: lead },
    { match: trail, guideAt: trail },
    { match: center, guideAt: center },
    attach,
  ]
}

/**
 * Ein Nachbar trägt zur Breiten-Fangregel bei einer geführten Kante bei —
 * geteilt zwischen der Notiz (prüft X UND Y, die Y-Distanz geht über
 * `scale` = `aspect` in eine Breite ein) und der Notiz (`getWidthResizeAlignment`
 * unten, prüft nur X, `scale` bleibt 1).
 *
 * Nimmt EINEN Nachbarn statt der ganzen Liste und wird vom Aufrufer je
 * Nachbar für X (und bei der Notiz danach für Y) aufgerufen — nicht diese
 * Funktion selbst über `others`. So bleibt die Rangfolge bei Gleichstand
 * (`<` in `consider`, siehe dort) exakt X-vor-Y JE Nachbar, wie vor dieser
 * Extraktion — eine Fassung, die intern über `others` liefe, prüfte
 * stattdessen X für ALLE Nachbarn vor Y für irgendeinen, und ein Gleichstand
 * zwischen der Y-Kante von Nachbar 1 und der X-Kante von Nachbar 2 entschiede
 * sich anders als vorher. Die Notiz muss dabei unverändert bleiben, deshalb
 * dieser Umweg.
 */
function considerEdgeWidthCandidates(
  o: Bounds,
  axis: 'x' | 'y',
  moving: MovingEdges,
  anchor: number,
  edge: number,
  consider: (width: number, distance: number) => void,
  scale = 1,
): void {
  const leadSide = axis === 'x' ? moving.x === 'left' : moving.y === 'top'
  for (const { match } of edgeTargets(o, axis, moving)) {
    // Nur auf der Seite, auf der die Kante überhaupt liegen kann — sonst
    // klappte das Element durch seinen Anker hindurch.
    if (leadSide ? match >= anchor : match <= anchor) continue
    consider(Math.abs(anchor - match) * scale, Math.abs(edge - match))
  }
}

/**
 * Ein Nachbar trägt zur Breiten-Fangregel über die GRÖSSE bei — sein eigenes
 * Breitenmaß, unabhängig davon, wo er liegt. Geteilt aus demselben Grund wie
 * `considerEdgeWidthCandidates`: Die Notiz ruft sie je Nachbar auf und
 * prüft danach noch dessen Höhe, die Notiz prüft nur sie.
 */
function considerSizeWidthCandidate(
  o: Bounds,
  rawWidth: number,
  consider: (width: number, distance: number) => void,
): void {
  const otherWidth = o.right - o.left
  consider(otherWidth, Math.abs(rawWidth - otherWidth))
}

/**
 * Richtet eine Notiz aus, dessen Größe gerade gezogen wird.
 *
 * Zwei Arten von Treffern, in dieser Rangfolge:
 *
 * 1. **Kante** — die geführte Kante rastet auf eine Kante, Mitte oder den
 *    festen Abstand eines Nachbarn ein. Zeigt eine Hilfslinie.
 * 2. **Größe** — Breite oder Höhe rastet auf die eines Nachbarn ein, egal wo
 *    der liegt. Zeigt ein Paar Maßlinien.
 *
 * Bei gleichem Abstand gewinnt die Kante: sie ist die sichtbarere Absicht.
 * Greift nichts, rastet die geführte Kante auf das Punktraster.
 *
 * Weil `keepAspectRatio` gilt, bleibt genau EIN Freiheitsgrad — die Breite.
 * Höhe, x und y folgen daraus und aus dem festen Anker.
 *
 * @param raw       die Lage, wie React Flow sie gerade meldet
 * @param moving    welche Kanten der Zeiger führt
 * @param others    die Nachbarn, an denen ausgerichtet werden darf
 * @param threshold Fangschwelle in CANVAS-Pixeln (`ALIGNMENT_THRESHOLD / zoom`)
 * @param grid      Rasterweite, wenn nichts greift
 * @param limits    kleinste und größte erlaubte Kantenlänge
 */
export function getResizeAlignment(
  raw: MovingRect,
  moving: MovingEdges,
  others: Bounds[],
  threshold: number,
  grid: number,
  limits: SizeLimits,
): ResizeAlignmentResult {
  const aspect = raw.height > 0 ? raw.width / raw.height : 1
  // Der Anker ist die Ecke, die stehen bleibt — alles wird von ihr aus gerechnet.
  const anchorX = moving.x === 'left' ? raw.x + raw.width : raw.x
  const anchorY = moving.y === 'top' ? raw.y + raw.height : raw.y
  const edgeX = moving.x === 'left' ? raw.x : raw.x + raw.width
  const edgeY = moving.y === 'top' ? raw.y : raw.y + raw.height

  let best: WidthCandidate | null = null
  const consider = (width: number, distance: number) => {
    if (width <= 0 || distance > threshold) return
    // `<` statt `<=`: bei Gleichstand gewinnt der zuerst geprüfte Kandidat.
    // Die Kanten laufen vor den Größen, sie gewinnen also den Gleichstand.
    if (!best || distance < best.distance) best = { width, distance }
  }

  for (const o of others) {
    considerEdgeWidthCandidates(o, 'x', moving, anchorX, edgeX, consider)
    considerEdgeWidthCandidates(o, 'y', moving, anchorY, edgeY, consider, aspect)
  }

  // Erst danach die Größen: bei gleichem Abstand soll die Kante gewinnen.
  for (const o of others) {
    considerSizeWidthCandidate(o, raw.width, consider)
    const otherHeight = o.bottom - o.top
    consider(otherHeight * aspect, Math.abs(raw.height - otherHeight))
  }

  const winner = best as WidthCandidate | null
  let width = winner ? winner.width : Math.abs(anchorX - snapToGrid(edgeX, grid))
  let height = width / aspect

  // `NodeResizeControl` erzwingt die Grenzen intern; unsere Korrektur käme
  // sonst daran vorbei. Zuerst über die Breite, dann über die Höhe — passt ein
  // Seitenverhältnis in keine der beiden Grenzen, gibt es nach.
  width = Math.min(Math.max(width, limits.min), limits.max)
  height = Math.min(Math.max(width / aspect, limits.min), limits.max)
  width = Math.min(Math.max(height * aspect, limits.min), limits.max)

  width = Math.round(width)
  height = Math.round(height)

  const rect: MovingRect = {
    width,
    height,
    x: moving.x === 'left' ? anchorX - width : anchorX,
    y: moving.y === 'top' ? anchorY - height : anchorY,
  }

  // Ohne Treffer war es reines Rastern — dann gibt es nichts zu zeigen. Ein
  // zufälliges Fluchten kommentarlos anzustreichen erklärte nichts.
  if (!winner) return { rect, snapped: false, vertical: [], horizontal: [], measures: [] }

  const finalEdgeX = moving.x === 'left' ? rect.x : rect.x + rect.width
  const finalEdgeY = moving.y === 'top' ? rect.y : rect.y + rect.height

  return {
    rect,
    snapped: true,
    vertical: collectResizeGuides(
      finalEdgeX,
      others,
      o => edgeTargets(o, 'x', moving),
      o => ({ start: o.top, end: o.bottom }),
      { start: rect.y, end: rect.y + rect.height },
    ),
    horizontal: collectResizeGuides(
      finalEdgeY,
      others,
      o => edgeTargets(o, 'y', moving),
      o => ({ start: o.left, end: o.right }),
      { start: rect.x, end: rect.x + rect.width },
    ),
    measures: collectMeasures(rect, others),
  }
}

/**
 * Die Linien einer Achse, auf denen die geführte Kante nach dem Einrasten
 * tatsächlich liegt. Wie beim Verschieben nach Position gruppiert: liegen
 * mehrere Nachbarn auf derselben Linie, wird daraus EINE über alle.
 */
function collectResizeGuides(
  edge: number,
  others: Bounds[],
  targets: (o: Bounds) => { match: number; guideAt: number }[],
  extent: (o: Bounds) => { start: number; end: number },
  mine: { start: number; end: number },
): Guide[] {
  const byPosition = new Map<number, { start: number; end: number }>()

  for (const o of others) {
    for (const { match, guideAt } of targets(o)) {
      if (!same(edge, match)) continue
      const e = extent(o)
      const existing = byPosition.get(guideAt)
      byPosition.set(
        guideAt,
        existing
          ? { start: Math.min(existing.start, e.start), end: Math.max(existing.end, e.end) }
          : {
              start: Math.min(mine.start, e.start),
              end: Math.max(mine.end, e.end),
            },
      )
    }
  }

  return [...byPosition.entries()].map(([position, span]) => ({ position, ...span }))
}

/**
 * Das Klammer-Paar, wenn die BREITE von `rect` mit `other` übereinstimmt —
 * sonst leer. Eine an der gezogenen, eine an der Partner-Notiz.
 *
 * Eigene Funktion statt inline in `collectMeasures`, weil sowohl die Notiz
 * (Breite UND Höhe) als auch die Notiz (nur Breite, `getWidthResizeAlignment`
 * unten) dieselbe Formel brauchen — einmal geschrieben statt zweimal.
 */
function widthMeasure(rect: MovingRect, other: Bounds): Measure[] {
  if (!same(rect.width, other.right - other.left)) return []
  return [
    { axis: 'x', from: rect.x, to: rect.x + rect.width, at: rect.y + rect.height },
    { axis: 'x', from: other.left, to: other.right, at: other.bottom },
  ]
}

/**
 * Dasselbe für die HÖHE. Nur die Notiz braucht sie — das Memo ändert ihre
 * Höhe in diesem Pfad nie, es gibt für sie also nichts zu vermessen.
 */
function heightMeasure(rect: MovingRect, other: Bounds): Measure[] {
  if (!same(rect.height, other.bottom - other.top)) return []
  return [
    { axis: 'y', from: rect.y, to: rect.y + rect.height, at: rect.x + rect.width },
    { axis: 'y', from: other.top, to: other.bottom, at: other.right },
  ]
}

/**
 * Sammelt die Klammern aller Nachbarn über die übergebenen Formeln, ohne
 * Dopplungen. Getrennt von `widthMeasure`/`heightMeasure`, damit die Notiz
 * (beide Formeln) und die Notiz (nur `widthMeasure`) dieselbe Sammel-Logik
 * teilen, ohne dieselbe Formel zweimal hinzuschreiben.
 */
function collectMeasuresWith(
  rect: MovingRect,
  others: Bounds[],
  formulas: ((rect: MovingRect, other: Bounds) => Measure[])[],
): Measure[] {
  const measures: Measure[] = []
  const seen = new Set<string>()

  const add = (measure: Measure) => {
    const key = `${measure.axis}|${measure.from}|${measure.to}|${measure.at}`
    if (seen.has(key)) return
    seen.add(key)
    measures.push(measure)
  }

  for (const o of others) {
    for (const formula of formulas) {
      for (const measure of formula(rect, o)) add(measure)
    }
  }

  return measures
}

/**
 * Die Klammern für jeden Nachbarn, dessen Breite oder Höhe die eingerastete
 * Größe trifft — je eine an ihm und eine an der gezogenen Notiz.
 */
function collectMeasures(rect: MovingRect, others: Bounds[]): Measure[] {
  return collectMeasuresWith(rect, others, [widthMeasure, heightMeasure])
}

/**
 * Richtet eine **Notiz** aus, deren BREITE gerade gezogen wird — der Gegenpart
 * zu `getResizeAlignment`, aber mit nur EINEM Freiheitsgrad.
 *
 * Die Notiz ist seitenverhältnistreu: Ein Treffer auf irgendeiner Achse
 * bestimmt die Breite, und Höhe/`y` folgen daraus über `aspect`. Die Notiz hat
 * kein Seitenverhältnis — ihre Höhe kommt vom Text (`useMeasuredNoteHeight`),
 * nicht vom Zeiger. `raw.height` und `raw.y` wandern deshalb UNVERÄNDERT ins
 * Ergebnis, und es gibt bewusst keine Kandidaten auf der Y-Achse: Eine
 * obere/untere Kante eines Nachbarn über ein erfundenes Seitenverhältnis auf
 * die Breite wirken zu lassen (wie `getResizeAlignment` es für die Notiz
 * tut) behauptete eine Beziehung, die für die Notiz nicht existiert.
 *
 * Genau das war der Fehler, bevor es diese Funktion gab: `CanvasFlow` rief für
 * JEDEN Resize `getResizeAlignment`, und dessen `height = width / aspect`
 * schrieb bei jedem Breitenzug eine erfundene Höhe und ein daraus
 * verschobenes `y` fest — der `ResizeObserver` korrigierte danach nur die
 * Höhe zurück, das `y` blieb für immer verschoben.
 *
 * Dieselbe Rangfolge wie bei der Notiz, nur auf eine Achse verkürzt: erst die
 * geführte Kante gegen Kante/Mitte/Anlegen eines Nachbarn, dann die Breite
 * gegen die eines Nachbarn, bei Gleichstand gewinnt die Kante. Greift nichts,
 * rastet die Kante auf das Punktraster.
 *
 * @param raw       die Lage, wie React Flow sie gerade meldet — `height` und
 *                  `y` werden UNVERÄNDERT ins Ergebnis übernommen
 * @param moving    welche der beiden senkrechten Kanten der Zeiger führt
 * @param others    die Nachbarn, an denen ausgerichtet werden darf
 * @param threshold Fangschwelle in CANVAS-Pixeln (`ALIGNMENT_THRESHOLD / zoom`)
 * @param grid      Rasterweite, wenn nichts greift
 * @param limits    kleinste und größte erlaubte Breite — die der NOTIZ
 *                  (`DEFAULT_MEMO_MIN_WIDTH`/`_MAX_WIDTH`), nicht die des
 *                  Notiz
 */
export function getWidthResizeAlignment(
  raw: MovingRect,
  moving: 'left' | 'right',
  others: Bounds[],
  threshold: number,
  grid: number,
  limits: SizeLimits,
  /**
   * Nur diese Breiten sind erlaubt. Gesetzt beim **Notizen**, der genau zwei
   * kennt (Quadrat oder Rechteck); bei der **Notiz** offen, ihre Breite ist
   * frei.
   *
   * Ist sie gesetzt, entfällt jedes Fangen: Es gibt nichts einzurasten, wenn
   * ohnehin nur zwei Werte in Frage kommen. Der Zug ist dann ein Schalter,
   * kein Regler — gewählt wird der nähere der beiden.
   */
  allowed?: number,
): ResizeAlignmentResult {
  const movingEdges: MovingEdges = { x: moving, y: 'top' }
  const anchorX = moving === 'left' ? raw.x + raw.width : raw.x
  const edgeX = moving === 'left' ? raw.x : raw.x + raw.width

  /*
    Eine feste Breite lässt nichts zu entscheiden übrig: kein Fangen an fremden
    Kanten, kein Raster, keine Grenzen (sie gelten bei der Notiz für die SEITE,
    siehe `stickyWidths`). Auch keine Hilfslinie — sie zeigte eine Ausrichtung
    an, die es nicht gibt.

    Die Maßklammern bleiben: Sie messen bloß und behaupten nichts.
  */
  if (allowed !== undefined) {
    const rect: MovingRect = {
      width: allowed,
      height: raw.height,
      x: moving === 'left' ? anchorX - allowed : anchorX,
      y: raw.y,
    }
    return {
      rect,
      snapped: false,
      vertical: [],
      horizontal: [],
      measures: collectMeasuresWith(rect, others, [widthMeasure]),
    }
  }

  let best: WidthCandidate | null = null
  const consider = (width: number, distance: number) => {
    if (width <= 0 || distance > threshold) return
    // `<` statt `<=`: bei Gleichstand gewinnt der zuerst geprüfte Kandidat —
    // die Kanten laufen vor der Größe, sie gewinnen also den Gleichstand.
    if (!best || distance < best.distance) best = { width, distance }
  }

  for (const o of others) {
    considerEdgeWidthCandidates(o, 'x', movingEdges, anchorX, edgeX, consider)
  }

  // Erst danach die Größe: bei gleichem Abstand soll die Kante gewinnen.
  for (const o of others) {
    considerSizeWidthCandidate(o, raw.width, consider)
  }

  /*
    Das **Punktraster** ist ein Kandidat wie jeder andere, nicht das Gesetz.

    Vorher galt es bedingungslos, sobald keine fremde Kante traf — die Kante
    stand damit bis zu einer halben Rasterweite neben dem Zeiger, an genau der
    Stelle, an der man gerade zieht. Beim **Notizen** bleibt das Rasten
    richtig (`getResizeAlignment`): Dort zieht man eine ECKE, und alle Ecken
    aller Zettel sollen auf denselben Punkten liegen. Hier zieht man eine
    KANTE, und die gehört unter den Finger.

    ZULETZT geprüft: Bei gleichem Abstand gewinnen die fremden Kanten und die
    Breiten der Nachbarn — sie sagen etwas über die Zeichnung, das Raster nur
    etwas über sich selbst.

    **Und mit einem eigenen, engeren Band.** Die gemeinsame Schwelle ist gegen
    den Zoom gerechnet (in `CanvasFlow` als `ALIGNMENT_THRESHOLD / zoom`),
    damit sich das Fangen auf dem Bildschirm überall gleich anfühlt. Fürs
    Raster geht das nicht auf: Herausgezoomt wächst das Band über eine halbe
    Rasterweite hinaus und deckt damit JEDE Stelle zwischen zwei Linien ab —
    die Kante rastete wieder bedingungslos, und der Fehler wäre nur in eine
    Zoomstufe verschoben. Ein Viertel der Rasterweite ist dagegen eine Länge
    in Canvas-Koordinaten, so wie das Raster selbst.
  */
  if (grid > 0) {
    const gridEdge = snapToGrid(edgeX, grid)
    const band = Math.min(threshold, grid / 4)
    if (Math.abs(edgeX - gridEdge) <= band) {
      consider(Math.abs(anchorX - gridEdge), Math.abs(edgeX - gridEdge))
    }
  }

  const winner = best as WidthCandidate | null
  // Ohne jeden Treffer folgt die Kante dem Zeiger — auf ganze Pixel gerundet,
  // damit ein Bruchteil aus der Zoom-Rechnung nicht als Zittern ankommt.
  let width = winner ? winner.width : raw.width

  // `NodeResizeControl` erzwingt die Grenzen intern; unsere Korrektur käme
  // sonst daran vorbei.
  width = Math.round(Math.min(Math.max(width, limits.min), limits.max))

  const rect: MovingRect = {
    width,
    // Unangetastet: Die Notiz zieht hier keine Höhe, das macht der Text.
    height: raw.height,
    x: moving === 'left' ? anchorX - width : anchorX,
    y: raw.y,
  }

  // Ohne Treffer war es reines Rastern — dann gibt es nichts zu zeigen.
  if (!winner) return { rect, snapped: false, vertical: [], horizontal: [], measures: [] }

  const finalEdgeX = moving === 'left' ? rect.x : rect.x + rect.width

  return {
    rect,
    snapped: true,
    vertical: collectResizeGuides(
      finalEdgeX,
      others,
      o => edgeTargets(o, 'x', movingEdges),
      o => ({ start: o.top, end: o.bottom }),
      { start: rect.y, end: rect.y + rect.height },
    ),
    // Keine waagerechte Ausrichtung — die Höhe ändert sich in diesem Pfad nie.
    horizontal: [],
    measures: collectMeasuresWith(rect, others, [widthMeasure]),
  }
}

/** Welche Achsen der Zeiger beim freien Skalieren überhaupt führt. */
export interface LedAxes {
  x: boolean
  y: boolean
}

/**
 * Richtet einen **Bereich** aus, dessen Größe FREI gezogen wird — beide Maße
 * unabhängig, kein Seitenverhältnis (CONTEXT.md, Bereich). Je Achse dieselbe
 * Rechnung wie für die Breite der Notiz (`getWidthResizeAlignment`): erst die
 * geführte Kante gegen Kante, Mitte oder Anlegen eines Nachbarn, dann die
 * Größe gegen die eines Nachbarn, zuletzt das Punktraster mit seinem engen
 * Band — und ohne Treffer folgt die Kante dem Zeiger, denn gezogen wird eine
 * KANTE, und die gehört unter den Finger.
 *
 * Die beiden Achsen entscheiden **unabhängig**: Rastet die rechte Kante an
 * einem Nachbarn ein, bleibt die Höhe, was der Zeiger sagt. Eine Achse, die
 * der Zeiger nicht führt (`axes`: an einer Kante nur eine, an einer Ecke
 * beide), wandert UNVERÄNDERT ins Ergebnis — und bekommt weder Linie noch
 * Klammer: Es hat sich dort nichts ausgerichtet.
 *
 * Nur ein Mindestmaß, keine Höchstgröße: Ein Bereich darf so groß sein, wie
 * man ihn zieht.
 */
export function getFreeResizeAlignment(
  raw: MovingRect,
  moving: MovingEdges,
  axes: LedAxes,
  others: Bounds[],
  threshold: number,
  grid: number,
  min: number,
): ResizeAlignmentResult {
  const anchorX = moving.x === 'left' ? raw.x + raw.width : raw.x
  const anchorY = moving.y === 'top' ? raw.y + raw.height : raw.y

  /*
    Eine Achse für sich: liefert die eingerastete Größe und ob ein Kandidat
    (Kante, Größe oder Raster) gegriffen hat. `size` ist Breite oder Höhe,
    `edge` die geführte Kante, `anchor` die stehende.
  */
  const settle = (axis: 'x' | 'y', anchor: number, edge: number, size: number): { size: number; snapped: boolean } => {
    let best: WidthCandidate | null = null
    const consider = (candidate: number, distance: number) => {
      if (candidate <= 0 || distance > threshold) return
      // `<` statt `<=`: bei Gleichstand gewinnt der zuerst geprüfte Kandidat —
      // die Kanten laufen vor der Größe, die Größe vor dem Raster.
      if (!best || distance < best.distance) best = { width: candidate, distance }
    }
    for (const o of others) considerEdgeWidthCandidates(o, axis, moving, anchor, edge, consider)
    for (const o of others) {
      const otherSize = axis === 'x' ? o.right - o.left : o.bottom - o.top
      consider(otherSize, Math.abs(size - otherSize))
    }
    // Das Raster als letzter Kandidat, mit dem engen Band — Begründung bei
    // `getWidthResizeAlignment`.
    if (grid > 0) {
      const gridEdge = snapToGrid(edge, grid)
      const band = Math.min(threshold, grid / 4)
      if (Math.abs(edge - gridEdge) <= band) consider(Math.abs(anchor - gridEdge), Math.abs(edge - gridEdge))
    }
    const winner = best as WidthCandidate | null
    // Auf ganze Pixel gerundet, damit ein Bruchteil aus der Zoom-Rechnung
    // nicht als Zittern ankommt; nie unter das Mindestmaß.
    return { size: Math.round(Math.max(winner ? winner.width : size, min)), snapped: Boolean(winner) }
  }

  const edgeX = moving.x === 'left' ? raw.x : raw.x + raw.width
  const edgeY = moving.y === 'top' ? raw.y : raw.y + raw.height
  const x = axes.x ? settle('x', anchorX, edgeX, raw.width) : { size: raw.width, snapped: false }
  const y = axes.y ? settle('y', anchorY, edgeY, raw.height) : { size: raw.height, snapped: false }

  const rect: MovingRect = {
    width: x.size,
    height: y.size,
    x: moving.x === 'left' ? anchorX - x.size : anchorX,
    y: moving.y === 'top' ? anchorY - y.size : anchorY,
  }

  if (!x.snapped && !y.snapped) return { rect, snapped: false, vertical: [], horizontal: [], measures: [] }

  const finalEdgeX = moving.x === 'left' ? rect.x : rect.x + rect.width
  const finalEdgeY = moving.y === 'top' ? rect.y : rect.y + rect.height

  return {
    rect,
    snapped: true,
    vertical: x.snapped
      ? collectResizeGuides(
          finalEdgeX,
          others,
          o => edgeTargets(o, 'x', moving),
          o => ({ start: o.top, end: o.bottom }),
          { start: rect.y, end: rect.y + rect.height },
        )
      : [],
    horizontal: y.snapped
      ? collectResizeGuides(
          finalEdgeY,
          others,
          o => edgeTargets(o, 'y', moving),
          o => ({ start: o.left, end: o.right }),
          { start: rect.x, end: rect.x + rect.width },
        )
      : [],
    measures: collectMeasuresWith(rect, others, [
      ...(x.snapped ? [widthMeasure] : []),
      ...(y.snapped ? [heightMeasure] : []),
    ]),
  }
}
